/**
 * CaseNavigator — the SidePanel's workspace-scope container.
 *
 * Layout (operator brief 2026-07-03): the case-workspace FILE TREE is the
 * STAR and takes all the remaining height (`flex-1`); the CASES pane is a
 * compact navigation convenience pinned to the bottom. It:
 *   - carries a distinctive REVERSE title bar (a filled dark bar with light
 *     text — inverted contrast — so the pane reads as its own zone). The
 *     **New case / New client** create affordances live right-aligned IN this
 *     bar (operator 2026-07-08 — blended into the CASOS headline rather than a
 *     separate row below it), spaced apart from the left title;
 *   - sits on a slightly WARMER background than the file-tree pane above it;
 *   - AUTO-ADJUSTS its height to its content, capped (`max-h`) so it never
 *     crowds the tree, and scrolls internally once the case list is long.
 *
 * This replaces the earlier fixed-ratio draggable split (`SplitRow` +
 * `useUIStore.sidebarSplitRatio`) — the operator wants the tree to always
 * own the majority of the column, not a persisted 75/25 the user must drag.
 *
 * The cases pane is a plain BLOCK scroll container (not a flex column) on
 * purpose: a `flex-1` child with `min-h-0` inside a content-sized parent
 * collapses to zero, so we let the section shrink-wrap its content instead
 * and cap it with `max-h`, with the reverse bar `sticky` to stay visible.
 *
 *   ┌─────────────────────────────────┐
 *   │ WORKSPACE TREE (flex-1 — star)  │  ← <CaseFileNav compact />
 *   ├─────────────────────────────────┤
 *   │ ▚ CASOS      + Novo caso  + …    │  ← sticky reverse bar (title left,
 *   │  ⌂ Global · cases (auto-height) │    create buttons right) + OverviewNav
 *   └─────────────────────────────────┘
 */

import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useNavigate } from "react-router-dom";
import { FolderTree, Plus, RefreshCw, Trash2 } from "lucide-react";
import { OverviewNav } from "@/components/shell/OverviewNav";
import { CaseFileNav } from "@/components/shell/CaseFileNav";
import { NewCaseDialog } from "@/components/context-pane/NewCaseDialog";
import { NewClientDialog } from "@/components/context-pane/NewClientDialog";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import {
  useUIStore,
  clampCasesPaneHeight,
  CASES_PANE_MIN_PX,
} from "@/stores/useUIStore";
import { cn } from "@/lib/utils";

/** Keyboard nudge for the resize handle, in px. Roughly one case row, so each
 *  press moves the boundary by a visible-but-controllable amount. */
const RESIZE_KEY_STEP_PX = 22;

export function CaseNavigator() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Manual refresh affordance (operator 2026-08-15) — see the bar button.
  const connectionState = useGatewayStore((s) => s.connectionState);
  const casesFetching = useIsFetching({ queryKey: ["overview-cases"] }) > 0;
  // Smart-default seeds for the global "New case" affordance (operator
  // 2026-07-06: if a client/matter is already in context, pre-fill).
  const selection = useActiveContextStore((s) => s.selection);
  const [newCaseOpen, setNewCaseOpen] = useState(false);
  const [newClientOpen, setNewClientOpen] = useState(false);

  // ── Resizable cases pane (operator 2026-07-27) ────────────────────────────
  // `null` = AUTO (content height, max-h capped) — the 2026-07-03 default,
  // preserved bit-for-bit until the lawyer actually drags.
  const casesPaneHeight = useUIStore((s) => s.casesPaneHeight);
  const setCasesPaneHeight = useUIStore((s) => s.setCasesPaneHeight);
  const columnRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  /** Measure rather than read state: in AUTO mode there IS no stored height, so
   *  the live rect is the only truth — and it is also what makes the first drag
   *  continue smoothly from wherever auto-layout had put the boundary. */
  const measuredPaneHeight = () => sectionRef.current?.getBoundingClientRect().height ?? 0;
  const columnHeight = () => columnRef.current?.getBoundingClientRect().height ?? 0;

  /** Clamp against the CURRENT column height — the ceiling is relative, so a
   *  window resize must not leave a stale absolute bound behind. */
  const commitHeight = (px: number) =>
    setCasesPaneHeight(clampCasesPaneHeight(px, columnHeight()));

  const resetToAuto = () => setCasesPaneHeight(null);

  function onHandlePointerDown(e: PointerEvent<HTMLDivElement>) {
    const startH = measuredPaneHeight();
    if (startH <= 0) return;
    dragRef.current = { startY: e.clientY, startH };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onHandlePointerMove(e: PointerEvent<HTMLDivElement>) {
    const s = dragRef.current;
    if (!s) return;
    // The handle is the pane's TOP edge, so dragging UP (negative dy) GROWS it.
    commitHeight(s.startH - (e.clientY - s.startY));
  }

  function endDrag(e: PointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  function onHandleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const current = measuredPaneHeight();
    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        commitHeight(current + RESIZE_KEY_STEP_PX);
        break;
      case "ArrowDown":
        e.preventDefault();
        commitHeight(current - RESIZE_KEY_STEP_PX);
        break;
      case "Home":
        e.preventDefault();
        commitHeight(CASES_PANE_MIN_PX);
        break;
      case "End":
        e.preventDefault();
        commitHeight(columnHeight()); // clamps down to the ceiling
        break;
      case "Enter":
      case " ":
        // Keyboard equivalent of double-click — the ONLY way back to AUTO
        // without a pointer, so it must exist.
        e.preventDefault();
        resetToAuto();
        break;
    }
  }

  // Create buttons styled for the REVERSE title bar — light mode is ink on a
  // light band, dark mode is light on dark. Both forks live here so the bar and
  // its buttons move together.
  //
  // ⚠ `normal-case` → `uppercase` 2026-09-07 (operator): the labels read
  // `+ CASO` / `+ CLIENTE`, matching the `CASOS` title beside them. `normal-case`
  // existed because "Novo caso" — two words — shouted in caps; with the label
  // cut to a single noun that reason is gone.
  // 🚨 `uppercase` must be stated EXPLICITLY, not inherited. Tailwind's
  // preflight sets `text-transform: none` on `button`, so simply DROPPING
  // `normal-case` leaves the buttons lower-case inside an `uppercase` bar —
  // measured in the browser (computed `text-transform: none` under a parent
  // computing `uppercase`), after I first assumed inheritance would carry it.
  // The two icon-only buttons (recycle bin, refresh) share this class and have
  // no text, so the change is inert for them.
  const barButtonCls =
    "inline-flex items-center gap-0.5 rounded border border-coffee-800/20 px-1.5 py-0.5 text-[10px] font-medium uppercase text-coffee-700 transition-colors hover:bg-coffee-800/10 hover:text-coffee-900 dark:border-cream-100/15 dark:text-cream-200/90 dark:hover:bg-coffee-700/80 dark:hover:text-cream-50";

  return (
    <div
      ref={columnRef}
      className="flex h-full min-h-0 flex-col"
      data-testid="case-navigator-split"
    >
      {/* Workspace file tree — the STAR; takes all remaining room. */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <CaseFileNav compact />
      </div>

      {/* Resize handle (operator 2026-07-27) — sits ON the boundary, replacing
          the section's own top border while dragging is possible. Gold on
          hover/focus is the SANCTIONED use (restyle directives §0: gold marks
          active/focus affordances); it is not the gold-as-paint this pass
          removed from labels and glyphs. */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label={t("workspace.caseNavigator.resizeHandle")}
        tabIndex={0}
        data-testid="cases-pane-resize-handle"
        data-dragging={dragging || undefined}
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={resetToAuto}
        onKeyDown={onHandleKeyDown}
        title={t("workspace.caseNavigator.resizeHandleHint")}
        className={cn(
          "relative shrink-0 basis-[5px] cursor-row-resize border-t border-cream-200/70 bg-transparent transition-colors",
          "hover:bg-gold-400/40 focus-visible:bg-gold-400/60 focus-visible:outline-none dark:border-coffee-700/60",
          dragging && "bg-gold-400/60",
        )}
      />

      {/* Cases pane — a compact navigation convenience. AUTO by default:
          content-height capped at max-h so the tree keeps priority (brief
          2026-07-03). Once the lawyer drags the handle it takes an explicit
          height instead and the cap no longer applies. Warmer background + a
          reverse title bar make it a distinct zone. */}
      <section
        ref={sectionRef}
        data-testid="case-navigator-cases-pane"
        data-cases-pane-height={casesPaneHeight == null ? "auto" : String(casesPaneHeight)}
        style={casesPaneHeight == null ? undefined : { height: casesPaneHeight }}
        // scroll-pt-7 leaves room for the sticky reverse title bar so the
        // active-case `scrollIntoView({block:"nearest"})` (OverviewNav) never
        // parks the focused row behind the bar.
        className={cn(
          "shrink-0 scroll-pt-7 overflow-y-auto bg-cream-100/60 dark:bg-coffee-900/50",
          // The cap applies ONLY in auto mode — once dragged, the explicit
          // height IS the intent and a 45% ceiling would silently override it.
          casesPaneHeight == null && "max-h-[45%]",
        )}
      >
        {/* REVERSE BAR — since 2026-09-07 (operator) light mode uses a light
            band (`bg-cream-100`) rather than inverting the theme; dark mode is
            unchanged. Keep this string in step with `CaseFileNav`'s title bar:
            the two are meant to read as the same heading family, and the
            case-summary strip in `CaseWorkspaceLayout` is ONE step deeper than
            both on purpose. */}
        <div className="sticky top-0 z-10 flex items-center gap-1.5 bg-cream-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-coffee-800 dark:bg-coffee-700 dark:text-cream-50">
          <FolderTree size={12} className="opacity-80" />
          <span>{t("workspace.caseNavigator.paneTitle")}</span>
          {/* Create affordances — right-aligned, spaced apart from the title. */}
          <div className="ml-auto flex items-center gap-1">
            {/* ⚖ SHORT LABELS, full phrase on hover (operator 2026-09-07).
                "Novo caso"/"Novo cliente" wrapped to two lines in this bar, so
                the `+` and the noun are what render — `+ CASO`, `+ CLIENTE` —
                and "Novo" survives only as the tooltip + accessible name.
                The `+` already says "new"; the word was carrying no
                information the icon did not.
                ⚠ The visible noun must stay a SUBSTRING of the aria-label
                (WCAG 2.5.3 label-in-name) — "Caso" inside "Novo caso" — so do
                not reword one half without the other. Uppercase is the bar's
                own `uppercase`, inherited now that `normal-case` is gone from
                `barButtonCls`; the underlying text is still "Caso", which is
                what assistive tech and the tests read. */}
            <button
              type="button"
              data-testid="overview-new-case"
              onClick={() => setNewCaseOpen(true)}
              title={t("workspaceTree.newCase")}
              aria-label={t("workspaceTree.newCase")}
              className={barButtonCls}
            >
              <Plus size={10} aria-hidden />
              {t("workspaceTree.newCaseShort")}
            </button>
            <button
              type="button"
              data-testid="overview-new-client"
              onClick={() => setNewClientOpen(true)}
              title={t("workspaceTree.newClient")}
              aria-label={t("workspaceTree.newClient")}
              className={barButtonCls}
            >
              <Plus size={10} aria-hidden />
              {t("workspaceTree.newClientShort")}
            </button>
            {/* The Recycle Bin's entry point. It belongs HERE, on the cases
                pane, and not under settings: restore is available to anyone
                who can delete, so a recovery path filed in an admin-shaped
                location is hidden from the people most likely to need it.
                Icon-only — recovery is rare, so it should not compete with
                the create actions for the bar's width. */}
            <button
              type="button"
              data-testid="overview-recycle-bin"
              onClick={() => navigate("/cases/recycle-bin")}
              title={t("workspaceTree.recycleBin")}
              aria-label={t("workspaceTree.recycleBin")}
              className={barButtonCls}
            >
              <Trash2 size={10} aria-hidden />
            </button>
            {/* Manual refresh (operator 2026-08-15,
                `lexide-workspace-refresh-miniplan.md` §3): re-read the cases
                list on demand. Reuses the EXACT key OverviewNav's own
                invalidations use — never a copy. Icon-only like the recycle
                bin; disabled while disconnected. */}
            <button
              type="button"
              data-testid="overview-refresh"
              onClick={() =>
                void queryClient.invalidateQueries({ queryKey: ["overview-cases"] })
              }
              disabled={connectionState !== "connected"}
              title={t("workspace.caseNavigator.refresh")}
              aria-label={t("workspace.caseNavigator.refresh")}
              className={cn(barButtonCls, "disabled:opacity-40")}
            >
              <RefreshCw
                size={10}
                aria-hidden
                className={cn(casesFetching && "animate-spin")}
              />
            </button>
          </div>
        </div>
        <OverviewNav compact />
      </section>

      {newCaseOpen && (
        <NewCaseDialog
          initialClientId={selection?.clientId ?? undefined}
          initialMatterId={selection?.matterId ?? undefined}
          onClose={() => setNewCaseOpen(false)}
          onCreated={() => {
            void queryClient.invalidateQueries({ queryKey: ["overview-cases"] });
          }}
        />
      )}
      {newClientOpen && <NewClientDialog onClose={() => setNewClientOpen(false)} />}
    </div>
  );
}
